import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCurrentMember, logout, isAuthenticated, Member } from '../services/api';
import { User, Phone, Mail, Calendar, Trophy, Medal, Award, Gift, Ticket, LogOut, ChevronRight, Clock, ShieldAlert } from 'lucide-react';

const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const [member, setMember] = useState<Member | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMemberData();
  }, []);

  const loadMemberData = async () => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }
    try {
      const data = await getCurrentMember();
      setMember(data);
    } catch (err) {
      navigate('/login');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to log out?')) {
      logout();
      navigate('/login');
    }
  };

  const getTierIcon = (tier: string) => {
    switch (tier) {
      case 'Gold': return Trophy;
      case 'Silver': return Medal;
      default: return Award;
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'Gold': return 'text-yellow-500';
      case 'Silver': return 'text-gray-400';
      default: return 'text-amber-700';
    }
  };

  const getTierBgColor = (tier: string) => {
    switch (tier) {
      case 'Gold': return 'bg-yellow-500';
      case 'Silver': return 'bg-gray-400';
      default: return 'bg-amber-700';
    }
  };

  const getTierLightBg = (tier: string) => {
    switch (tier) {
      case 'Gold': return 'bg-yellow-50';
      case 'Silver': return 'bg-gray-50';
      default: return 'bg-amber-50';
    }
  };

  const getNextTier = (current: string) => {
    if (current === 'Bronze') return { tier: 'Silver', points: 300 };
    if (current === 'Silver') return { tier: 'Gold', points: 600 };
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-buggy-blue mx-auto mb-4"></div>
          <p className="text-buggy-gray">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (!member) return null;

  const TierIcon = getTierIcon(member.currentTier);
  const tierColor = getTierColor(member.currentTier);
  const tierBgColor = getTierBgColor(member.currentTier);
  const tierLightBg = getTierLightBg(member.currentTier);
  const nextTier = getNextTier(member.currentTier);
  const progress = nextTier ? (member.currentPoints / nextTier.points) * 100 : 100;
  const pointsToNext = nextTier ? nextTier.points - member.currentPoints : 0;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Sticky Header */}
      <div className="sticky top-0 z-40">
        <div className="bg-buggy-blue text-white p-6">
          <div className="max-w-md mx-auto">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold">My Profile</h1>
                <p className="text-sm opacity-90">Manage your account</p>
              </div>
              <div className="flex flex-col items-end">
                <div className="inline-flex items-center gap-1.5 bg-white rounded-full px-3 py-1.5 mb-2 shadow-sm">
                  <TierIcon size={16} strokeWidth={2.5} className={tierColor} />
                  <span className="text-xs font-semibold text-buggy-dark">{member.currentTier} Tier</span>
                </div>
                <p className="text-xs opacity-75">Your Points</p>
                <p className="text-2xl font-bold">{member.currentPoints}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto pb-20">
        <div className="max-w-md mx-auto p-4 space-y-4">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className={`${tierBgColor} px-6 py-5`}>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                <User size={32} strokeWidth={2} className="text-white" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-white">{member.firstName} {member.lastName}</h2>
                <div className="flex items-center gap-1.5 mt-1">
                  <TierIcon size={16} strokeWidth={2.5} className="text-white/90" />
                  <span className="text-white/90 text-sm font-medium">{member.currentTier} Tier Member</span>
                </div>
              </div>
            </div>
          </div>

          <div className="p-5 border-b border-gray-100">
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Phone size={18} strokeWidth={2} className="text-buggy-gray" />
                </div>
                <div>
                  <p className="text-xs text-buggy-gray">Phone Number</p>
                  <p className="text-sm font-medium text-buggy-dark">{member.phoneNumber}</p>
                </div>
              </div>
              {member.email && (
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center">
                    <Mail size={18} strokeWidth={2} className="text-buggy-gray" />
                  </div>
                  <div>
                    <p className="text-xs text-buggy-gray">Email</p>
                    <p className="text-sm font-medium text-buggy-dark">{member.email}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Calendar size={18} strokeWidth={2} className="text-buggy-gray" />
                </div>
                <div>
                  <p className="text-xs text-buggy-gray">Member Since</p>
                  <p className="text-sm font-medium text-buggy-dark">
                    {new Date(member.enrolledDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 divide-x divide-gray-100">
            <div className="p-5 text-center">
              <p className="text-3xl font-bold text-buggy-blue">{member.currentPoints}</p>
              <p className="text-xs text-buggy-gray mt-1">Current Points</p>
            </div>
            <div className="p-5 text-center">
              <p className="text-3xl font-bold text-buggy-dark">{member.lifetimePoints}</p>
              <p className="text-xs text-buggy-gray mt-1">Lifetime Points</p>
            </div>
          </div>
        </div>

        <div className={`${tierLightBg} rounded-xl p-5`}>
          {nextTier ? (
            <>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold text-buggy-dark">Progress to {nextTier.tier}</span>
                <span className="text-sm text-buggy-gray">{member.currentPoints} / {nextTier.points} pts</span>
              </div>
              <div className="w-full bg-white rounded-full h-3 mb-3 shadow-inner">
                <div className={`${tierBgColor} h-3 rounded-full`} style={{ width: `${Math.min(progress, 100)}%` }}></div>
              </div>
              <p className="text-sm text-center text-buggy-dark">
                <span className="font-semibold">{pointsToNext} more points</span> until {nextTier.tier} tier
              </p>
            </>
          ) : (
            <div className="text-center py-2">
              <Trophy size={32} strokeWidth={2} className="text-yellow-500 mx-auto mb-2" />
              <p className="font-semibold text-buggy-dark">You've reached the highest tier!</p>
              <p className="text-sm text-buggy-gray mt-1">Enjoy all exclusive Gold benefits</p>
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          <p className="px-5 pt-4 pb-2 text-xs font-semibold text-buggy-gray uppercase tracking-wide">Quick Actions</p>
          <button onClick={() => navigate('/rewards')} className="w-full flex items-center justify-between p-4 hover:bg-gray-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-buggy-blue/10 rounded-lg flex items-center justify-center">
                <Gift size={20} strokeWidth={2} className="text-buggy-blue" />
              </div>
              <span className="font-medium text-buggy-dark">Browse Rewards</span>
            </div>
            <ChevronRight size={20} strokeWidth={2} className="text-gray-400" />
          </button>
          <div className="border-t border-gray-100"></div>
          <button onClick={() => navigate('/redemptions')} className="w-full flex items-center justify-between p-4 hover:bg-gray-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-buggy-blue/10 rounded-lg flex items-center justify-center">
                <Ticket size={20} strokeWidth={2} className="text-buggy-blue" />
              </div>
              <span className="font-medium text-buggy-dark">View Redemptions</span>
            </div>
            <ChevronRight size={20} strokeWidth={2} className="text-gray-400" />
          </button>
          <div className="border-t border-gray-100"></div>
          <button onClick={() => navigate('/transactions')} className="w-full flex items-center justify-between p-4 hover:bg-gray-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-buggy-blue/10 rounded-lg flex items-center justify-center">
                <Clock size={20} strokeWidth={2} className="text-buggy-blue" />
              </div>
              <span className="font-medium text-buggy-dark">View Points History</span>
            </div>
            <ChevronRight size={20} strokeWidth={2} className="text-gray-400" />
          </button>
          <div className="border-t border-gray-100"></div>
          <button onClick={() => navigate('/health')} className="w-full flex items-center justify-between p-4 hover:bg-gray-50">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-50 rounded-lg flex items-center justify-center">
                <ShieldAlert size={20} strokeWidth={2} className="text-red-500" />
              </div>
              <span className="font-medium text-buggy-dark">Account Health</span>
            </div>
            <ChevronRight size={20} strokeWidth={2} className="text-gray-400" />
          </button>
        </div>

        <button onClick={handleLogout} className="w-full flex items-center justify-center gap-2 p-4 bg-white rounded-xl shadow-md text-red-600 hover:bg-red-50 font-medium">
          <LogOut size={20} strokeWidth={2} />
          Log Out
        </button>

        <div className="text-center text-xs text-buggy-gray pt-2 pb-4">
          <p>InsiderPass for BuggyBusters</p>
          <p className="mt-0.5 opacity-75">Version 1.0.0</p>
        </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;
